// src/dashboard/updateDailyStatus.js

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import MayoLogo from '../assets/images/Mayo.png';
import '../global/styles.css';

/**
 * ShiftCounter
 * Click +BLS / +ALS repeatedly to set how many units are available for that shift.
 */
function ShiftCounter({ label, value, onChange }) {
  const bls = value?.bls ?? 0;
  const als = value?.als ?? 0;

  const inc = (key) => onChange({ ...value, [key]: (value?.[key] ?? 0) + 1 });
  const dec = (key) => onChange({ ...value, [key]: Math.max(0, (value?.[key] ?? 0) - 1) });
  const reset = () => onChange({ bls: 0, als: 0 });

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #eee',
        borderRadius: 12,
        padding: 16,
        marginBottom: 12
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ color: '#0E3293' }}>{label}</strong>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={reset}
          style={{ padding: '6px 10px', fontSize: 12 }}
        >
          Reset
        </button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>BLS</div>
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>{bls}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button type="button" className="btn btn-primary" onClick={() => inc('bls')}>
              + BLS
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => dec('bls')}>
              -
            </button>
          </div>
        </div>

        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>ALS</div>
          <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>{als}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button type="button" className="btn btn-primary" onClick={() => inc('als')}>
              + ALS
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => dec('als')}>
              -
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function UpdateDailyStatus({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Core fields
  const [censusLevel, setCensusLevel] = useState('');

  // Shift counts (clickable)
  const [shiftCounts, setShiftCounts] = useState({
    shift_7a_7p: { bls: 0, als: 0 },
    shift_11a_11p: { bls: 0, als: 0 },
    shift_7p_7a: { bls: 0, als: 0 }
  });

  // Transport activity
  const [transportsScheduled, setTransportsScheduled] = useState('');
  const [cancellationsYesterday, setCancellationsYesterday] = useState('');

  const [notes, setNotes] = useState('');

  const censusOptions = useMemo(
    () => [
      { label: 'Green', color: '#28a745' },
      { label: 'Yellow', color: '#ffc107' },
      { label: 'Red', color: '#dc3545' },
      { label: 'Purple', color: '#6f42c1' },
      { label: 'Black', color: '#343a40' }
    ],
    []
  );

  const formatShiftString = ({ bls = 0, als = 0 }) => {
    if (!bls && !als) return '';
    if (bls && als) return `BLS: ${bls} | ALS: ${als}`;
    if (bls) return `BLS: ${bls}`;
    return `ALS: ${als}`;
  };

  const parseShiftString = (s) => {
    // Supports "BLS: 2 | ALS: 1" or "BLS: 2" or "ALS: 1"
    if (!s || typeof s !== 'string') return { bls: 0, als: 0 };
    const blsMatch = s.match(/BLS:\s*(\d+)/i);
    const alsMatch = s.match(/ALS:\s*(\d+)/i);
    return {
      bls: blsMatch ? Number(blsMatch[1]) : 0,
      als: alsMatch ? Number(alsMatch[1]) : 0
    };
  };

  useEffect(() => {
    const loadLatest = async () => {
      setLoading(true);
      setMessage('');

      try {
        const { data, error } = await supabase
          .from('daily_status')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;

        if (data && data.length) {
          const latest = data[0];

          setCensusLevel(latest.census_level || '');

          setShiftCounts({
            shift_7a_7p: parseShiftString(latest.shift_7a_7p_units),
            shift_11a_11p: parseShiftString(latest.shift_11a_11p_units),
            shift_7p_7a: parseShiftString(latest.shift_7p_7a_units)
          });

          setTransportsScheduled(latest.transports_scheduled ?? '');
          setCancellationsYesterday(latest.cancellations ?? '');
          setNotes(latest.notes ?? '');
        }
      } catch (err) {
        console.error('Error loading latest status:', err);
        setMessage('Failed to load latest status. You can still submit a new update.');
      } finally {
        setLoading(false);
      }
    };

    loadLatest();
  }, []);

  const toIntOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return n;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      // Minimal validation
      if (!censusLevel) {
        setMessage('Please select a Census Level.');
        setSaving(false);
        return;
      }

      const payload = {
        census_level: censusLevel,

        // Unit availability removed: keep columns null so public page can ignore them
        bls_units: null,
        als_units: null,

        // store strings in the existing columns so PublicDailyStatus works without changes
        shift_7a_7p_units: formatShiftString(shiftCounts.shift_7a_7p),
        shift_11a_11p_units: formatShiftString(shiftCounts.shift_11a_11p),
        shift_7p_7a_units: formatShiftString(shiftCounts.shift_7p_7a),

        transports_scheduled: toIntOrNull(transportsScheduled),
        cancellations: toIntOrNull(cancellationsYesterday),

        notes: notes || null,

        updated_by_first_name: userInfo?.firstName || null,
        updated_by_last_name: userInfo?.lastName || null
      };

      const { error } = await supabase.from('daily_status').insert([payload]);

      if (error) throw error;

      setMessage('Daily status updated successfully.');
    } catch (err) {
      console.error('Error saving daily status:', err);
      setMessage(err?.message || 'Failed to save daily status.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-center">
      <HamburgerMenu isAdmin={isAdmin} onLogout={onLogout} onNavigate={onNavigate} />

      <div className="card card-center" style={{ maxWidth: 700, width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <img src={MayoLogo} alt="Mayo Clinic" style={{ width: 110, marginBottom: 10 }} />
          <h1 className="title" style={{ marginBottom: 6 }}>
            Update Daily Status
          </h1>
          <div style={{ color: '#666', fontSize: 13 }}>
            Logged in as: {userInfo?.firstName || ''} {userInfo?.lastName || ''}
          </div>
        </div>

        {message && (
          <div
            style={{
              background: message.toLowerCase().includes('fail') ? '#fdecea' : '#e8f5e9',
              border: `1px solid ${message.toLowerCase().includes('fail') ? '#f5c2c7' : '#c8e6c9'}`,
              color: message.toLowerCase().includes('fail') ? '#842029' : '#1b5e20',
              borderRadius: 10,
              padding: 12,
              marginBottom: 16,
              fontSize: 14
            }}
          >
            {message}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Census Level */}
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 10, color: '#333' }}>Census Level</h3>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {censusOptions.map((opt) => {
                  const selected = censusLevel === opt.label;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setCensusLevel(opt.label)}
                      title={opt.label}
                      style={{
                        width: 54,
                        height: 54,
                        borderRadius: 12,
                        border: selected ? '3px solid #0E3293' : '1px solid #ddd',
                        background: opt.color,
                        cursor: 'pointer',
                        boxShadow: selected ? '0 0 0 3px rgba(14,50,147,0.15)' : 'none'
                      }}
                    />
                  );
                })}
              </div>
              <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>
                Selected: <strong>{censusLevel || 'None'}</strong>
              </div>
            </div>

            {/* Shift Information */}
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 10, color: '#333' }}>Shift Information</h3>

              <ShiftCounter
                label="7a - 7p"
                value={shiftCounts.shift_7a_7p}
                onChange={(v) => setShiftCounts((p) => ({ ...p, shift_7a_7p: v }))}
              />
              <ShiftCounter
                label="11a - 11p"
                value={shiftCounts.shift_11a_11p}
                onChange={(v) => setShiftCounts((p) => ({ ...p, shift_11a_11p: v }))}
              />
              <ShiftCounter
                label="7p - 7a"
                value={shiftCounts.shift_7p_7a}
                onChange={(v) => setShiftCounts((p) => ({ ...p, shift_7p_7a: v }))}
              />
            </div>

            {/* Transport Activity */}
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 10, color: '#333' }}>Transport Activity</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 700 }}>
                    Scheduled for today
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={transportsScheduled}
                    onChange={(e) => setTransportsScheduled(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 700 }}>
                    Cancellations from yesterday
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={cancellationsYesterday}
                    onChange={(e) => setCancellationsYesterday(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #ddd'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                Note: “On calendar” is no longer needed since “Scheduled for today” covers that.
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 18 }}>
              <h3 style={{ marginBottom: 10, color: '#333' }}>Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                placeholder="Optional notes..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Saving...' : 'Submit Update'}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => onNavigate('home')}
                style={{ flex: 1 }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default UpdateDailyStatus;
